import * as React from "react";
import { ShoppingCart, User, MapPin, Package, ChevronDown, ShoppingBag, Settings, Store, UserPlus, LogOut, Languages, DollarSign, Percent, Menu, X, Home, Grid, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchAutocomplete } from "@/components/SearchAutocomplete";
import { ImpersonationBanner } from "@/components/ImpersonationBanner";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import NotificationsDropdown from "@/components/notifications/NotificationsDropdown";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuPortal,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/hooks/useAuth";
import { useEffectiveProfile } from "@/hooks/useEffectiveProfile";
import { useCart } from "@/hooks/useCartSync";
import { useLanguage } from '@/hooks/useLanguage';
import { useCurrency } from "@/hooks/useCurrency";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState } from "react";

const Header = () => {
  const { user, signOut, isEffectiveVendor, profile: authProfile } = useAuth();
  const { profile: effectiveProfile } = useEffectiveProfile();
  const { getTotalItems, setIsOpen } = useCart();
  const { t, language, setLanguage } = useLanguage();
  const { displayCurrency, setDisplayCurrency } = useCurrency();
  const { settings } = useSiteSettings();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <>
      <ImpersonationBanner />
      <header style={{ top: 'var(--banner-height, 0px)' }} className="sticky z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        {/* Mobile Header */}
        <div className="lg:hidden">
          {/* Top Bar - Logo, Cart, Account */}
          <div className="flex h-14 items-center justify-between px-4 border-b">
            {/* Menu Hamburger + Logo */}
            <div className="flex items-center gap-2">
              <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="sm" className="p-2">
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-[280px] p-0">
                  <SheetHeader className="p-4 border-b">
                    <SheetTitle className="text-left">
                      {user ? (
                        <div className="flex flex-col">
                          <span className="font-semibold">{t('header.hello')}, {effectiveProfile?.full_name?.split(' ')[0] || t('common.user')}</span>
                          <span className="text-xs text-muted-foreground">{effectiveProfile?.email || user.email}</span>
                        </div>
                      ) : (
                        <span>{t('header.menu')}</span>
                      )}
                    </SheetTitle>
                  </SheetHeader>
                  
                  {/* Mobile Menu Content */}
                  <div className="flex flex-col py-2">
                    {/* Language & Currency Settings */}
                    <div className="px-4 py-3 border-b bg-muted/30">
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Languages className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm font-medium">{t('language')}</span>
                          </div>
                          <Select value={language} onValueChange={setLanguage}>
                            <SelectTrigger className="h-8 w-[110px] text-xs">
                              <SelectValue>
                                {language === 'fr' && '🇫🇷 Français'}
                                {language === 'en' && '🇬🇧 English'}
                                {language === 'lg-fr' && '🇨🇩 Lingala'}
                                {language === 'sw' && '🇹🇿 Swahili'}
                              </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="fr">🇫🇷 Français</SelectItem>
                              <SelectItem value="en">🇬🇧 English</SelectItem>
                              <SelectItem value="lg-fr">🇨🇩 Lingala</SelectItem>
                              <SelectItem value="sw">🇹🇿 Swahili</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <DollarSign className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm font-medium">{t('currency')}</span>
                          </div>
                          <Select value={displayCurrency} onValueChange={setDisplayCurrency}>
                            <SelectTrigger className="h-8 w-[110px] text-xs">
                              <SelectValue>
                                {displayCurrency === 'CDF' && 'FC CDF'}
                                {displayCurrency === 'USD' && '$ USD'}
                                {displayCurrency === 'EUR' && '€ EUR'}
                              </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="CDF">FC Franc Congolais</SelectItem>
                              <SelectItem value="USD">$ Dollar US</SelectItem>
                              <SelectItem value="EUR">€ Euro</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                    {!user && (
                      <Link 
                        to="/auth" 
                        className="flex items-center gap-3 px-4 py-3 hover:bg-accent"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <User className="h-5 w-5" />
                        <span className="font-medium">{t('header.login')}</span>
                      </Link>
                    )}
                    
                    <Link 
                      to="/" 
                      className="flex items-center gap-3 px-4 py-3 hover:bg-accent"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <Home className="h-5 w-5" />
                      <span>{t('header.home')}</span>
                    </Link>
                    
                    <Link 
                      to="/products" 
                      className="flex items-center gap-3 px-4 py-3 hover:bg-accent"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <Grid className="h-5 w-5" />
                      <span>{t('header.all_products')}</span>
                    </Link>
                    
                    {settings.show_deals_menu === true && (
                      <Link 
                        to="/deals" 
                        className="flex items-center gap-3 px-4 py-3 hover:bg-accent"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <Percent className="h-5 w-5" />
                        <span>{t('header.deals')}</span>
                      </Link>
                    )}
                    
                    <Link 
                      to="/relay-points" 
                      className="flex items-center gap-3 px-4 py-3 hover:bg-accent"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <MapPin className="h-5 w-5" />
                      <span>{t('header.points')}</span>
                    </Link>
                    
                    {user && (
                      <>
                        <div className="border-t my-2"></div>
                        
                        <Link 
                          to="/dashboard" 
                          className="flex items-center gap-3 px-4 py-3 hover:bg-accent"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          <Settings className="h-5 w-5" />
                          <span>{t('header.my_profile')}</span>
                        </Link>
                        
                        <Link 
                          to="/orders" 
                          className="flex items-center gap-3 px-4 py-3 hover:bg-accent"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          <ShoppingBag className="h-5 w-5" />
                          <span>{t('header.my_orders')}</span>
                        </Link>
                        
                        <Link 
                          to="/my-reviews" 
                          className="flex items-center gap-3 px-4 py-3 hover:bg-accent"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          <Star className="h-5 w-5" />
                          <span>Mes avis</span>
                        </Link>
                        
                        {isEffectiveVendor && (
                          <Link 
                            to="/vendor-account" 
                            className="flex items-center gap-3 px-4 py-3 hover:bg-accent"
                            onClick={() => setMobileMenuOpen(false)}
                          >
                            <Store className="h-5 w-5" />
                            <span>{t('header.vendor_dashboard')}</span>
                          </Link>
                        )}
                        
                        {!isEffectiveVendor && (
                          <Link 
                            to="/become-vendor" 
                            className="flex items-center gap-3 px-4 py-3 hover:bg-accent"
                            onClick={() => setMobileMenuOpen(false)}
                          >
                            <UserPlus className="h-5 w-5" />
                            <span>{t('header.become_vendor')}</span>
                          </Link>
                        )}
                        
                        {(effectiveProfile?.system_role === 'admin' || effectiveProfile?.system_role === 'hr' || effectiveProfile?.system_role === 'super_admin') && (
                          <Link 
                            to="/admin" 
                            className="flex items-center gap-3 px-4 py-3 hover:bg-accent"
                            onClick={() => setMobileMenuOpen(false)}
                          >
                            <Settings className="h-5 w-5" />
                            <span>{t('header.administration')}</span>
                          </Link>
                        )}
                        
                        {/* Gestion des relais - Toujours visible pour super_admin, admin et relay */}
                        {(effectiveProfile?.system_role === 'relay' || effectiveProfile?.system_role === 'super_admin' || effectiveProfile?.system_role === 'admin' || authProfile?.has_vendor_management_access) && (
                          <Link 
                            to="/relay" 
                            className="flex items-center gap-3 px-4 py-3 hover:bg-accent"
                            onClick={() => setMobileMenuOpen(false)}
                          >
                            <MapPin className="h-5 w-5" />
                            <span>Gestion des relais</span>
                          </Link>
                        )}
                        
                        <div className="border-t my-2"></div>
                        
                        <button 
                          onClick={() => {
                            signOut();
                            setMobileMenuOpen(false);
                          }}
                          className="flex items-center gap-3 px-4 py-3 hover:bg-accent text-red-600"
                        >
                          <LogOut className="h-5 w-5" />
                          <span>{t('header.logout')}</span>
                        </button>
                      </>
                    )}
                  </div>
                </SheetContent>
              </Sheet>
              
              <Link to="/" className="flex items-center">
                {settings.logo_url ? (
                  <img 
                    src={settings.logo_url} 
                    alt={settings.site_name} 
                    className="h-8 w-auto object-contain"
                  />
                ) : (
                  <div className="flex items-center gap-1">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-primary">
                      <Package className="h-5 w-5 text-primary-foreground" />
                    </div>
                    <span className="text-lg font-bold">{settings.site_name}</span>
                  </div>
                )}
              </Link>
            </div>
          </div>
          
          {/* Search Bar - Full Width on Mobile */}
          <div className="px-4 py-2 border-b">
            <SearchAutocomplete />
          </div>
        </div>
        
        {/* Desktop Header - Original */}
        <div className="hidden lg:flex container h-16 items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center space-x-2">
          {settings.logo_url ? (
            <img 
              src={settings.logo_url} 
              alt={settings.site_name} 
              className="h-10 w-auto object-contain"
            />
          ) : (
            <>
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-primary">
                <Package className="h-6 w-6 text-primary-foreground" />
              </div>
              <h1 className="text-2xl font-bold bg-gradient-primary bg-clip-text text-transparent">
                {settings.site_name}
              </h1>
            </>
          )}
        </Link>

        {/* Search Bar with Autocomplete */}
        <SearchAutocomplete />

        {/* Navigation Actions */}
        <div className="flex items-center space-x-4">
          <Link to="/relay-points">
            <Button variant="ghost" size="sm" className="hidden lg:flex">
              <MapPin className="h-4 w-4 mr-2" />
              {t('header.points')}
            </Button>
          </Link>
          
          {settings.show_deals_menu === true && (
            <Link to="/deals">
              <Button variant="ghost" size="sm" className="hidden lg:flex">
                <Percent className="h-4 w-4 mr-2" />
                {t('header.deals')}
              </Button>
            </Link>
          )}
          
          {!isEffectiveVendor && (
            <Link to="/become-vendor">
              <Button variant="ghost" size="sm" className="hidden lg:flex">
                {t('header.become_vendor')}
              </Button>
            </Link>
          )}
          
          
          <Button
            variant="ghost" 
            size="sm" 
            className="relative"
            onClick={() => setIsOpen(true)}
          >
            <ShoppingCart className="h-4 w-4" />
            <span className="ml-2 hidden sm:inline">{t('header.cart')}</span>
            {getTotalItems() > 0 && (
              <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs">
                {getTotalItems()}
              </Badge>
            )}
          </Button>

          {user && <NotificationsDropdown />}
          
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2">
                  <User className="h-4 w-4" />
                  <span className="hidden sm:inline">
                    {t('header.hello')}, {effectiveProfile?.full_name?.split(' ')[0] || t('employee.user')}
                  </span>
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 max-h-96 overflow-visible">
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium leading-none">
                      {effectiveProfile?.full_name || t('common.user')}
                    </p>
                    <p className="text-xs leading-none text-muted-foreground">
                      {effectiveProfile?.email || user.email}
                    </p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>
                    <User className="mr-2 h-4 w-4" />
                    {t('header.buyer_account')}
                  </DropdownMenuSubTrigger>
                  <DropdownMenuPortal>
                    <DropdownMenuSubContent>
                      <DropdownMenuItem asChild>
                        <Link to="/dashboard" className="w-full cursor-pointer">
                          <Settings className="mr-2 h-4 w-4" />
                          {t('header.my_profile')}
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/orders" className="w-full cursor-pointer">
                          <ShoppingBag className="mr-2 h-4 w-4" />
                          {t('header.my_orders')}
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/my-reviews" className="w-full cursor-pointer">
                          <Star className="mr-2 h-4 w-4" />
                          {t('header.my_reviews', 'Mes avis')}
                        </Link>
                      </DropdownMenuItem>
                    </DropdownMenuSubContent>
                  </DropdownMenuPortal>
                </DropdownMenuSub>
                
                {isEffectiveVendor && (
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger>
                      <Store className="mr-2 h-4 w-4" />
                      {t('header.vendor_account')}
                    </DropdownMenuSubTrigger>
                    <DropdownMenuPortal>
                      <DropdownMenuSubContent>
                        <DropdownMenuItem asChild>
                          <Link to="/vendor-account" className="w-full cursor-pointer">
                            <Store className="mr-2 h-4 w-4" />
                            {t('header.vendor_dashboard')}
                          </Link>
                        </DropdownMenuItem>
                      </DropdownMenuSubContent>
                    </DropdownMenuPortal>
                  </DropdownMenuSub>
                )}
                
                {(effectiveProfile?.system_role === 'admin' || effectiveProfile?.system_role === 'hr' || effectiveProfile?.system_role === 'super_admin') && (
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger>
                      <Settings className="mr-2 h-4 w-4" />
                      {t('header.admin_account')}
                    </DropdownMenuSubTrigger>
                    <DropdownMenuPortal>
                      <DropdownMenuSubContent>
                        <DropdownMenuItem asChild>
                          <Link to="/admin" className="w-full cursor-pointer">
                            <Settings className="mr-2 h-4 w-4" />
                            {t('header.administration')}
                          </Link>
                        </DropdownMenuItem>
                      </DropdownMenuSubContent>
                    </DropdownMenuPortal>
                  </DropdownMenuSub>
                )}
                
                {(effectiveProfile?.system_role === 'relay' || effectiveProfile?.system_role === 'super_admin' || effectiveProfile?.system_role === 'admin' || authProfile?.has_vendor_management_access) && (
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger>
                      <MapPin className="mr-2 h-4 w-4" />
                      {t('header.relay_management')}
                    </DropdownMenuSubTrigger>
                    <DropdownMenuPortal>
                      <DropdownMenuSubContent>
                        <DropdownMenuItem asChild>
                          <Link to="/relay" className="w-full cursor-pointer">
                            <Package className="mr-2 h-4 w-4" />
                            {t('header.relay_dashboard')}
                          </Link>
                        </DropdownMenuItem>
                      </DropdownMenuSubContent>
                    </DropdownMenuPortal>
                  </DropdownMenuSub>
                )}
                
                {!isEffectiveVendor && effectiveProfile?.system_role !== 'admin' && effectiveProfile?.system_role !== 'super_admin' && (
                  <DropdownMenuItem asChild>
                    <Link to="/become-vendor" className="w-full cursor-pointer">
                      <UserPlus className="mr-2 h-4 w-4" />
                      {t('header.create_business')}
                    </Link>
                  </DropdownMenuItem>
                )}
                
                
                <DropdownMenuSeparator />
                
                <div className="px-2 py-1.5">
                  <div className="flex items-center justify-between space-x-4">
                    <div className="flex items-center space-x-2">
                      <Languages className="h-3 w-3 text-muted-foreground" />
                      <Select value={language} onValueChange={setLanguage}>
                        <SelectTrigger className="h-7 w-16 text-xs border-0 bg-transparent p-0 focus:ring-0">
                          <SelectValue>
                            {language === 'fr' && 'FR'}
                            {language === 'en' && 'EN'}  
                            {language === 'lg-fr' && 'LIN'}
                            {language === 'sw' && 'SW'}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="fr">🇫🇷 Français</SelectItem>
                          <SelectItem value="en">🇬🇧 English</SelectItem>
                          <SelectItem value="lg-fr">🇨🇩 Lingala</SelectItem>
                          <SelectItem value="sw">🇹🇿 Swahili</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <DollarSign className="h-3 w-3 text-muted-foreground" />
                      <Select value={displayCurrency} onValueChange={setDisplayCurrency}>
                        <SelectTrigger className="h-7 w-14 text-xs border-0 bg-transparent p-0 focus:ring-0">
                          <SelectValue>
                            {displayCurrency === 'CDF' && 'CDF'}
                            {displayCurrency === 'USD' && 'USD'}
                            {displayCurrency === 'EUR' && 'EUR'}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="CDF">FC Franc Congolais</SelectItem>
                          <SelectItem value="USD">$ Dollar US</SelectItem>
                          <SelectItem value="EUR">€ Euro</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
                
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={() => signOut()}
                  className="text-red-600 focus:text-red-600 cursor-pointer"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  {t('header.logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link to="/auth">
              <Button variant="outline" size="sm">
                <User className="h-4 w-4" />
                <span className="ml-2 hidden sm:inline">{t('header.login')}</span>
              </Button>
            </Link>
          )}
        </div>
      </div>
    </header>
    </>
  );
};

export default Header;